import React, { useState } from "react";
import {
  LoadingSpinner,
  MeatballMenu,
  MeatballMenuItem,
  PrimaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useAzureIndexesApi from "@/hooks/useAzureIndexesApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { AzureIndex } from "../types";
import IndexModal from "./IndexModal";
import DeleteIndexModal from "./DeleteIndexModal";
import IconEditPencil from "@/icons/IconEditPencil";
import IconDeactivateUserPerson from "@/icons/IconDeactivateUserPerson";

export default function AINewIndexes() {
  const { azureIndexes, loading, add, update, remove } = useAzureIndexes();
  const { createIndex, editIndex, deleteIndex } = useAzureIndexesApi();
  const { subscription } = useSubscriptions();
  const toastAlert = useToastAlert();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<AzureIndex | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<AzureIndex | null>(null);

  // Handle Create
  const handleCreate = async (index: AzureIndex) => {
    const result = await createIndex({
      name: index.name,
      type: index.type,
      searchSize: index.searchSize,
      semanticSearchEnabled: index.semanticSearchEnabled
    });

    if (result.success && result.data) {
      add(result.data);
      toastAlert.displayToast(Severity.Success, "Index created successfully");
      setShowCreateModal(false);
    } else {
      toastAlert.displayToast(
        Severity.Error,
        result.error || "Failed to create index"
      );
    }
  };

  // Handle Edit
  const handleEdit = async (index: AzureIndex) => {
    const result = await editIndex({
      id: index.id,
      name: index.name,
      type: index.type,
      searchSize: index.searchSize,
      semanticSearchEnabled: index.semanticSearchEnabled
    });

    if (result.success && result.data) {
      update(result.data);
      toastAlert.displayToast(Severity.Success, "Index updated successfully");
      setEditingIndex(null);
    } else {
      toastAlert.displayToast(
        Severity.Error,
        result.error || "Failed to update index"
      );
    }
  };

  // Handle Delete
  const handleDelete = async (id: number) => {
    const result = await deleteIndex(id);

    if (result.success) {
      remove(id);
      toastAlert.displayToast(Severity.Success, "Index deleted successfully");
      setDeletingIndex(null);
    } else {
      toastAlert.displayToast(
        Severity.Error,
        result.error || "Failed to delete index"
      );
    }
  };

  // Meatball menu items
  const getMeatballItems = (index: AzureIndex): MeatballMenuItem[] => [
    {
      icon: IconEditPencil,
      label: "Edit Index",
      onClick: () => setEditingIndex(index)
    },
    {
      icon: IconDeactivateUserPerson,
      label: "Delete Index",
      onClick: () => setDeletingIndex(index)
    }
  ];

  // Check if at subscription limit
  const atLimit =
    subscription?.maxIndexCount !== undefined &&
    azureIndexes.length >= subscription.maxIndexCount;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-semibold">Knowledge Base Indexes</h1>
          <p className="text-sm text-slate-500">
            Manage Azure Search indexes for AI training data
          </p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className="text-sm text-slate-500">
            {azureIndexes.length} / {subscription?.maxIndexCount ?? "∞"} indexes
          </span>
          <PrimaryBlueButton
            label="Create Index"
            onClick={() => setShowCreateModal(true)}
            disabled={atLimit}
          />
        </div>
      </div>

      {/* Limit Warning */}
      {atLimit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-700">
            You have reached your subscription limit for indexes. Please upgrade
            your plan or delete unused indexes to create new ones.
          </p>
        </div>
      )}

      {/* Empty State */}
      {azureIndexes.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center">
          <div className="text-slate-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6M12 9v6"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">
            No indexes yet
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Create your first Azure Search index to enable AI-powered search over
            your training data.
          </p>
          <PrimaryBlueButton
            label="Create Your First Index"
            onClick={() => setShowCreateModal(true)}
          />
        </div>
      ) : (
        /* Index Table */
        <div className="bg-white border rounded-lg shadow-sm overflow-visible">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">
                  Name
                </th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">
                  Type
                </th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">
                  Search Size
                </th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">
                  AI Enhanced
                </th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600 w-12">
                  {/* Actions */}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {azureIndexes.map((index) => (
                <tr key={index.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-medium text-sm">{index.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">{index.type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {index.searchSize}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        index.semanticSearchEnabled
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {index.semanticSearchEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <MeatballMenu menuItems={getMeatballItems(index)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          How indexes work
        </h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>
            • Indexes are created from your uploaded training files in blob storage
          </li>
          <li>
            • Documents are automatically chunked and converted to vector embeddings
          </li>
          <li>
            • The AI chatbot uses these indexes to find relevant context for answers
          </li>
          <li>
            • Search Size controls how many results are retrieved (higher = more context)
          </li>
        </ul>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <IndexModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}

      {editingIndex && (
        <IndexModal
          index={editingIndex}
          onClose={() => setEditingIndex(null)}
          onSave={handleEdit}
        />
      )}

      {deletingIndex && (
        <DeleteIndexModal
          index={deletingIndex}
          onClose={() => setDeletingIndex(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
